import { HOUSE_LABEL_ALIASES, HOUSE_SHORTHAND } from './catalogAliases.js'
import { CATALOG_HOUSE_NAMES } from './catalogHouseNames.js'
import { ruleRank } from './catalogRuleRank.js'

// Collection-entry search matching for FOS-08-SEARCH-FIX-001.
//
// The production search box calls search_catalog_findability_v1, then
// reranks on the client. Two gaps in that path drop bottles the catalog
// already has:
//   1. French elisions (L'Homme), N° signs, and glued digit-letter tokens
//      (9AM, M7) never retrieve the row, so rerank has nothing to promote.
//   2. A typed concentration ties the base bottle on the server, and the
//      popular concentration stays first.
//
// This module does not replace the typed query. It recognizes a strict
// bottle match (same identity, and the same concentration when one was
// typed) and can build extra recovery searches. Callers may prepend a
// recovery row only when that row is itself a strict match, so a near
// miss stays empty instead of becoming a wrong bottle.
//
// strictNameMatch is wider than the merged #779 exact core. It also
// accepts a line prefix, a house word anywhere in the name, a trailing
// modifier, a token reordering, or one one-letter typo. The tightest
// match stays first. exactNameMatch is the regression base for that
// merged path.

const ACCENT_FROM = 'àáâäãåèéêëìíîïòóôöõùúûüçñÀÁÂÄÃÅÈÉÊËÌÍÎÏÒÓÔÖÕÙÚÛÜÇÑ’°'
const ACCENT_TO = "aaaaaaeeeeiiiiooooouuuucnaaaaaaeeeeiiiiooooouuuucn'o"

// Longer phrases first. Mirrors the collection-entry scorer: "s" and "the"
// are noise tokens, and "parfum intense" is one token before bare "parfum".
const NOISE = '(?:eau de parfum intense|edp intense|eau de parfum|eau de toilette|extrait de parfum|eau de cologne|extrait|edp|edt|parfum intense|parfum|cologne|perfume spray|perfume|spray|s|the)'
const CONC_RULES = [
  [/\b(?:eau de parfum intense|edp intense)\b/, 'EDPI'],
  [/\b(?:eau de parfum|edp)\b/, 'EDP'],
  [/\b(?:eau de toilette|edt)\b/, 'EDT'],
  [/\b(?:extrait|parfum|le parfum|essence de parfum)\b/, 'P'],
  [/\b(?:cologne|eau de cologne)\b/, 'C'],
]

// Whole-query only. A general one- or two-character search stays blocked.
export const SHORT_NAME_ALIASES = Object.freeze({
  y: 'yves saint laurent y',
  w: 'arabian oud w',
  // A two-character trigram scan times out. These are whole catalog names.
  m7: 'yves saint laurent m7',
  si: 'giorgio armani si',
  pi: 'givenchy pi',
})

const HOUSE_GROUPS = [
  ['dior', 'christian dior'],
  ['yves saint laurent', 'ysl', 'saint laurent'],
  ['giorgio armani', 'emporio armani', 'armani'],
  ['jean paul gaultier', 'gaultier', 'jpg'],
  ['hermes'],
  ['parfums de marly', 'de marly', 'pdm'],
  ['by kilian', 'kilian'],
  ['viktor rolf', 'viktor and rolf'],
  ['louis vuitton', 'lv'],
  ['dolce and gabbana', 'dolce gabbana', 'd and g', 'dg'],
  ['maison francis kurkdjian', 'mfk'],
  ['tom ford', 'tf'],
  ['carolina herrera'],
  ['the dua brand', 'dua'],
  ['maison margiela', 'margiela'],
  ['jo malone', 'jo malone london'],
  ['chloe'],
]

function translateAccents(value) {
  let out = ''
  for (const ch of String(value || '')) {
    const at = ACCENT_FROM.indexOf(ch)
    out += at === -1 ? ch : ACCENT_TO[at]
  }
  return out
}

// Same fold the collection-entry walk uses before it compares bottles.
export function auditNormalize(value) {
  const folded = translateAccents(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  return folded.replace(/\bno ?(\d+)\b/g, 'no $1')
}

export function stripNoise(normalized) {
  let text = String(normalized || '').replace(/\s+/g, ' ').trim()
  for (let pass = 0; pass < 2; pass += 1) {
    text = ` ${text} `.replace(new RegExp(` ${NOISE} `, 'g'), ' ').replace(/\s+/g, ' ').trim()
  }
  return text
}

export function concentrationCode(value) {
  const text = auditNormalize(value)
  if (!text) return null
  for (const [pattern, code] of CONC_RULES) {
    if (pattern.test(text)) return code
  }
  return null
}

function containsPhrase(hay, needle) {
  if (!hay || !needle) return false
  return ` ${hay} `.includes(` ${needle} `)
}

function housePhrases(house) {
  const normalized = auditNormalize(house)
  const phrases = new Set()
  if (normalized) phrases.add(normalized)
  const group = HOUSE_GROUPS.find(aliases => aliases.some(alias => alias === normalized || containsPhrase(normalized, alias)))
  for (const alias of group || []) if (alias.length >= 4) phrases.add(alias)
  const last = normalized.split(' ').filter(word => word.length >= 4).pop()
  if (last) phrases.add(last)
  return [...phrases].sort((a, b) => b.length - a.length)
}

function stripLeadingHouse(core, house) {
  if (!core) return core
  for (const phrase of housePhrases(house)) {
    if (core === phrase) return ''
    if (core.startsWith(`${phrase} `)) return core.slice(phrase.length + 1).trim()
  }
  return core
}

// "Gabrielle Chanel" is the bottle Gabrielle. The house word is not always a prefix.
function stripHouseAnywhere(core, house) {
  let text = core
  for (const phrase of housePhrases(house)) {
    text = ` ${text} `.replaceAll(` ${phrase} `, ' ')
  }
  return text.replace(/\s+/g, ' ').trim()
}

const TRAILING_MODIFIERS = ['edition', 'lux', 'nyc', 'man', 'woman', 'men', 'women', 'homme', 'femme']
const GENDER_MODIFIERS = new Set(['man', 'woman', 'men', 'women', 'homme', 'femme'])

function dropTrailingModifiers(core) {
  let text = String(core || '')
  let changed = true
  while (changed) {
    changed = false
    for (const modifier of TRAILING_MODIFIERS) {
      if (text.endsWith(` ${modifier}`)) {
        const next = text.slice(0, -(modifier.length + 1)).trim()
        const remaining = next.split(' ').filter(Boolean)
        // "212 NYC" is not the bottle "212". "212 Men NYC" is "212 Men".
        if (modifier === 'nyc' && remaining.length < 2) continue
        // "Enigma Pour Femme" is not "Enigma Pour Homme". Both would
        // otherwise collapse to "Enigma Pour".
        const previous = remaining[remaining.length - 1]
        if (GENDER_MODIFIERS.has(modifier) && (previous === 'pour' || previous === 'for')) continue
        text = next
        changed = true
      }
    }
  }
  return text
}

function dedupeTrailingRepeat(core) {
  const parts = String(core || '').split(' ').filter(Boolean)
  if (parts.length > 1 && parts.slice(0, -1).includes(parts[parts.length - 1])) parts.pop()
  return parts.join(' ')
}

// "Encre Noire for Men" is Encre Noire. "Pour Homme" is not "Pour Femme",
// so the French pair stays on the core.
function dropForGenderPhrase(core) {
  let text = String(core || '').trim()
  for (const phrase of ['for women', 'for woman', 'for men', 'for man']) {
    if (!text.endsWith(` ${phrase}`)) continue
    const next = text.slice(0, -(phrase.length + 1)).trim()
    if (next) text = next
  }
  return text
}

function genderOfRaw(raw) {
  const text = String(raw || '')
  if (/(?:^| )(?:pour femme|for women|for woman|for her|femme|women|woman|her)$/.test(text)) return 'women'
  if (/(?:^| )(?:pour homme|for men|for man|for him|homme|men|man|him)$/.test(text)) return 'men'
  return null
}

// Gender words the typed query can carry. "for men" is not in this list:
// "Encre Noire for Men" still matches Encre Noire. Femme, Women, Her, Homme,
// and Him do not match a bottle that leaves that gender off.
const LISTED_WOMEN = /(?:^| )(?:pour femme|femme|women|woman|her)(?: |$)/
const LISTED_MEN = /(?:^| )(?:pour homme|homme|him)(?: |$)/

export function listedQueryGender(query) {
  const q = auditNormalize(query)
  const women = LISTED_WOMEN.test(q)
  const men = LISTED_MEN.test(q)
  if (women === men) return null
  return women ? 'women' : 'men'
}

// "Dior Homme Intense" and "Her Confession" carry the gender word even
// though it is not the last token. "Code" does not.
function nameCarriesGender(row, gender) {
  const name = auditNormalize(row?.name)
  if (!name || !gender) return false
  if (gender === 'women') return LISTED_WOMEN.test(name)
  if (gender === 'men') return LISTED_MEN.test(name)
  return false
}

function identityParts(value, house) {
  const base = stripNoise(auditNormalize(value))
  // A bottle named for its house ("Carolina Herrera") must keep that name.
  // Any other query drops the house word wherever it sits ("Gabrielle Chanel").
  let text = stripHouseAnywhere(base, house) || base
  text = text.replace(/\b(?:and|by)\b/g, ' ').replace(/\bno \d+\b/g, ' ').replace(/\s+/g, ' ').trim()
  text = dedupeTrailingRepeat(text)
  return { gender: genderOfRaw(text), core: dropForGenderPhrase(dropTrailingModifiers(text)) }
}

function canonIdentity(value, house) {
  return identityParts(value, house).core
}

function tokenKey(core) {
  return String(core || '').split(' ').filter(Boolean).sort().join(' ')
}

function editDistance(a, b) {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index)
  for (let i = 1; i <= a.length; i += 1) {
    let previous = i - 1
    row[0] = i
    for (let j = 1; j <= b.length; j += 1) {
      const next = a[i - 1] === b[j - 1] ? previous : Math.min(previous, row[j - 1], row[j]) + 1
      previous = row[j]
      row[j] = next
    }
  }
  return row[b.length]
}

// A one-word prefix that names a different bottle, not a line.
// "Paris" is not "Mon Paris". "Velours" is not "Rouge Velours".
const NOT_A_LINE_PREFIX = new Set([
  'mon', 'rouge', 'bleu', 'noir', 'noire', 'blanc', 'blanche', 'original',
  'gold', 'white', 'black', 'red', 'pink', 'vert', 'rose', 'pure',
])

// A line prefix (Emir, Hermessence, Shaghaf) or one trailing modifier may sit
// on the catalog name. The typed tokens themselves still have to agree.
function isAffixIdentity(shorter, longer) {
  if (!shorter || !longer || !longer.endsWith(` ${shorter}`)) return false
  const extra = longer.slice(0, longer.length - shorter.length).trim().split(' ').filter(Boolean)
  const shortTokens = shorter.split(' ').filter(Boolean)
  if (!extra.length || extra.length > 4 || !shortTokens.length) return false
  if (extra.includes('impression')) return false
  if (shortTokens.length === 1 && shortTokens[0].length < 4) return false
  if (shortTokens.length === 1 && extra.length > 2) return false
  if (extra.length === 1 && (extra[0].length < 4 || NOT_A_LINE_PREFIX.has(extra[0]))) return false
  // A line prefix adds words. It does not repeat the typed name inside a
  // longer flanker ("L'Eau Kenzo Pour Homme" is not "L'Eau par Kenzo Love").
  if (extra.some(token => shortTokens.includes(token))) return false
  return true
}

// A long typed title may be the start of the catalog name ("I Don't Need a
// Prince…" before "…To Be A Princess"). Short queries stay suffix-only, so
// "Paris" still does not match "Mon Paris".
function isPrefixIdentity(shorter, longer) {
  if (!shorter || !longer || !longer.startsWith(`${shorter} `)) return false
  const extra = longer.slice(shorter.length).trim().split(' ').filter(Boolean)
  const shortTokens = shorter.split(' ').filter(Boolean)
  if (shortTokens.length < 4 || !extra.length || extra.length > 8) return false
  if (extra.includes('impression')) return false
  return true
}

function oneTokenTypo(a, b) {
  const left = String(a || '').split(' ').filter(Boolean)
  const right = String(b || '').split(' ').filter(Boolean)
  if (!left.length || left.length !== right.length) return false
  let typos = 0
  for (let i = 0; i < left.length; i += 1) {
    if (left[i] === right[i]) continue
    if (left[i].length < 4 || right[i].length < 4 || editDistance(left[i], right[i]) !== 1) return false
    typos += 1
  }
  return typos === 1
}

export function rowConcentrationCode(row) {
  return concentrationCode(row?.name) || concentrationCode(row?.concentration) || null
}

// Strict bottle match: identity cores agree, and a typed concentration agrees.
// The row's own house is peeled off both sides so "Armani Code" matches a
// catalog row named "Code" filed under Giorgio Armani.
// Exact bottle identity from FOS-08: same core after house-prefix removal.
// The wider strictNameMatch below also accepts the walk's line-prefix and
// trailing-modifier equivalences. This one is the merged-app regression base.
export function exactNameMatch(query, row) {
  if (!row || !row.name) return false
  const queryNorm = auditNormalize(query)
  const nameNorm = auditNormalize(row.name)
  const queryCore = stripNoise(queryNorm)
  const nameCore = stripNoise(nameNorm)
  const house = auditNormalize(row.house)
  const strippedQuery = stripLeadingHouse(queryCore, house)
  const strippedName = stripLeadingHouse(nameCore, house)
  const same = queryCore === nameCore || strippedQuery === strippedName
  if (!same || !queryCore) return false
  const wanted = concentrationCode(queryNorm)
  if (!wanted) return true
  return wanted === rowConcentrationCode(row)
}

export function strictNameMatch(query, row) {
  if (!row || !row.name) return false
  const house = auditNormalize(row.house)
  const queryParts = identityParts(query, house)
  const nameParts = identityParts(row.name, house)
  // "Euphoria Women" is not Euphoria Men. A query with no gender word still
  // matches ("Euphoria", "Jubilation XXV Man"). A listed gender word
  // (Femme, Women, Her, Homme, Him) does not match the ungendered bottle.
  const listed = listedQueryGender(query)
  if (listed && !nameCarriesGender(row, listed)) return false
  if (queryParts.gender && nameParts.gender && queryParts.gender !== nameParts.gender) return false
  const queryCore = queryParts.core
  const nameCore = nameParts.core
  const legacyQuery = stripLeadingHouse(stripNoise(auditNormalize(query)), house)
  const legacyName = stripLeadingHouse(stripNoise(auditNormalize(row.name)), house)
  const same = (queryCore && (queryCore === nameCore
    || tokenKey(queryCore) === tokenKey(nameCore)
    || isAffixIdentity(queryCore, nameCore)
    || isPrefixIdentity(queryCore, nameCore)
    || oneTokenTypo(queryCore, nameCore)))
    || (legacyQuery && legacyQuery === legacyName)
  if (!same || !(queryCore || legacyQuery)) return false
  const wanted = concentrationCode(auditNormalize(query))
  if (!wanted) return true
  return wanted === rowConcentrationCode(row)
}

// Identity the 77a3d66 search box used. No prefix, no "for men" collapse,
// and no gender rejection. The guard compares a new top against this order.
function shippedCore(value, house) {
  const base = stripNoise(auditNormalize(value))
  let text = stripHouseAnywhere(base, house) || base
  text = text.replace(/\b(?:and|by)\b/g, ' ').replace(/\bno \d+\b/g, ' ').replace(/\s+/g, ' ').trim()
  return dedupeTrailingRepeat(dropTrailingModifiers(text))
}

export function shippedNameMatch(query, row) {
  if (!row || !row.name) return false
  const house = auditNormalize(row.house)
  const queryCore = shippedCore(query, house)
  const nameCore = shippedCore(row.name, house)
  const legacyQuery = stripLeadingHouse(stripNoise(auditNormalize(query)), house)
  const legacyName = stripLeadingHouse(stripNoise(auditNormalize(row.name)), house)
  const same = (queryCore && (queryCore === nameCore
    || tokenKey(queryCore) === tokenKey(nameCore)
    || isAffixIdentity(queryCore, nameCore)
    || oneTokenTypo(queryCore, nameCore)))
    || (legacyQuery && legacyQuery === legacyName)
  if (!same || !(queryCore || legacyQuery)) return false
  const wanted = concentrationCode(auditNormalize(query))
  if (!wanted) return true
  return wanted === rowConcentrationCode(row)
}

function shippedIdentityRank(query, row) {
  if (!shippedNameMatch(query, row)) return null
  const house = auditNormalize(row.house)
  const queryCore = shippedCore(query, house)
  const nameCore = shippedCore(row.name, house)
  if (queryCore && queryCore === nameCore) return 0
  if (queryCore && tokenKey(queryCore) === tokenKey(nameCore)) return 1
  if (queryCore && isAffixIdentity(queryCore, nameCore)) return 2
  if (queryCore && oneTokenTypo(queryCore, nameCore)) return 3
  return 4
}

function promoteShipped(query, rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? rows : []
  const ranked = rows.map((row, index) => ({ row, index, rank: shippedIdentityRank(query, row) }))
  const matches = ranked.filter(item => item.rank !== null)
  if (!matches.length) return rows
  matches.sort((a, b) => a.rank - b.rank || a.index - b.index)
  const rest = ranked.filter(item => item.rank === null)
  return [...matches, ...rest].map(item => item.row)
}

export function selectShipped(query, primaryRows = [], secondRows = [], recoveryBatches = [], expandedQuery = query) {
  let rows = promoteShipped(query, ruleRank(query, primaryRows, secondRows))
  if (rows.some(row => shippedNameMatch(query, row))) return rows
  for (const batch of recoveryBatches) {
    if (batch?.rpc === 'v2' && !v2RecoveryAllowed(query, expandedQuery)) continue
    const strictRows = (batch?.rows || []).filter(row => shippedNameMatch(query, row))
    if (strictRows.length) return promoteShipped(query, [...strictRows, ...rows])
  }
  return rows
}

function identityRank(query, row) {
  if (!strictNameMatch(query, row)) return null
  const house = auditNormalize(row.house)
  const queryParts = identityParts(query, house)
  const nameParts = identityParts(row.name, house)
  const queryCore = queryParts.core
  const nameCore = nameParts.core
  const genderFit = queryParts.gender && nameParts.gender !== queryParts.gender ? 1 : 0
  let rank = 4
  let extra = 0
  if (queryCore && queryCore === nameCore) rank = 0
  else if (queryCore && tokenKey(queryCore) === tokenKey(nameCore)) rank = 1
  else if (queryCore && isAffixIdentity(queryCore, nameCore)) {
    rank = 2
    extra = queryCore ? nameCore.slice(0, nameCore.length - queryCore.length).trim().split(' ').filter(Boolean).length : 0
  } else if (queryCore && isPrefixIdentity(queryCore, nameCore)) {
    rank = 2
    extra = nameCore.slice(queryCore.length).trim().split(' ').filter(Boolean).length
  } else if (queryCore && oneTokenTypo(queryCore, nameCore)) rank = 3
  return { rank, extra, genderFit }
}

export function promoteStrictMatches(query, rows = []) {
  if (!Array.isArray(rows) || rows.length < 2) return Array.isArray(rows) ? rows : []
  const ranked = rows.map((row, index) => ({ row, index, rank: identityRank(query, row) }))
  const matches = ranked.filter(item => item.rank !== null)
  if (!matches.length) return rows
  matches.sort((a, b) => a.rank.rank - b.rank.rank || a.rank.extra - b.rank.extra || a.rank.genderFit - b.rank.genderFit || a.index - b.index)
  const rest = ranked.filter(item => item.rank === null)
  return [...matches, ...rest].map(item => item.row)
}

// Another lookup is worth it when nothing matches, or when the typed gender
// has no equal-core row yet ("Code Femme" must not stay on "Code" once
// "Code for Women" can be retrieved).
export function recoveryStillNeeded(query, rows = []) {
  const matched = (rows || []).map(row => identityRank(query, row)).filter(Boolean)
  if (!matched.length) return true
  const queryGender = identityParts(query, '').gender
  if (!queryGender) return false
  return !matched.some(rank => rank.rank === 0 && rank.genderFit === 0)
}

// Space a French elision (L'Homme, d'Hermès). Possessives (Don't, Angels',
// Poseidon's) stay put: the letter before the apostrophe is not a lone
// elision consonant at a word boundary.
export function spaceFrenchElisions(value) {
  return String(value || '')
    .replace(/(^|\s)([ldjnmstc]|qu)['’]/gi, '$1$2 ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function foldNumero(value) {
  return String(value || '')
    .replace(/n\s*[°º˚]\s*/gi, 'No ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function hasDigitLetterJoin(value) {
  return /\d[A-Za-z]|[A-Za-z]\d/.test(String(value || ''))
}

// search_catalog_v2 is the unsplit lookup for a glued token such as 9AM.
// Skip it when the client already rewrote the query to a real bottle
// ("br540" → Baccarat Rouge 540): v2 on the shorthand returns a different bottle.
export function v2RecoveryAllowed(query, expandedQuery) {
  const rawKey = String(query || '').trim().toLowerCase().replace(/[.’']/g, '')
  const expanded = String(expandedQuery || '').trim().toLowerCase()
  return rawKey === expanded
}

export function shouldSearchQuery(value) {
  const q = String(value || '').trim()
  if (q.length >= 3) return true
  return Boolean(SHORT_NAME_ALIASES[auditNormalize(q)])
}

// Primary lookup. Y, W, M7, Sì, and Pi go out as the full catalog name,
// because a one- or two-character trigram scan is blocked or times out.
// Every other query keeps today's findability call.
export function routePrimarySearch(value) {
  const q = String(value || '').trim()
  const key = auditNormalize(q)
  if (SHORT_NAME_ALIASES[key]) return { rpc: 'findability', term: SHORT_NAME_ALIASES[key] }
  return { rpc: 'findability', term: q }
}

// Extra searches used only when the primary window has no strict match.
// Each returned row still has to strict-match before it may be shown.
export function recoveryPlan(value) {
  const q = String(value || '').trim()
  if (!q) return []
  const steps = []
  const seen = new Set()
  const push = (rpc, term) => {
    const key = `${rpc}:${term}`
    if (!term || seen.has(key)) return
    seen.add(key)
    steps.push({ rpc, term })
  }
  const spaced = spaceFrenchElisions(q)
  if (spaced !== q) push('findability', spaced)
  const folded = foldNumero(q)
  if (folded !== q) push('findability', folded)
  if (spaced !== q) {
    const both = foldNumero(spaced)
    if (both !== spaced && both !== q) push('findability', both)
  }
  if (hasDigitLetterJoin(q)) push('v2', q)
  const alias = SHORT_NAME_ALIASES[auditNormalize(q)]
  if (alias) push('findability', alias)
  if (spaced !== q || folded !== q) {
    const core = stripNoise(auditNormalize(q))
    const spacedNorm = auditNormalize(spaced)
    if (core.length >= 3 && core !== auditNormalize(q) && core !== spacedNorm) push('findability', core)
  }
  return steps
}

function housePartMatches(expected, actual) {
  if (!expected || !actual) return false
  if (expected === actual) return true
  if (containsPhrase(actual, expected) || containsPhrase(expected, actual)) return true
  const group = HOUSE_GROUPS.find(aliases => aliases.some(alias => alias === expected || containsPhrase(expected, alias)))
  if (!group) return false
  return group.some(alias => alias === actual || containsPhrase(actual, alias) || containsPhrase(alias, actual))
}

export function housesMatch(expected, actual) {
  const parts = String(expected || '').split('/').map(part => auditNormalize(part)).filter(Boolean)
  const actualNorm = auditNormalize(actual)
  if (!parts.length || !actualNorm) return false
  return parts.some(part => housePartMatches(part, actualNorm))
}

// The seven spellings the 2026-10-02 rescore counted as the same bottle.
const HAND_FOUND = [
  { house: 'Afnan', q: 'Supremacy Collector Edition', name: 'supremacy collector s edition pour homme' },
  { house: 'Paris Corner', q: 'North Stag Expressions IV Quarte', name: 'north stag expressions iv quatre' },
  { house: 'Carolina Herrera', q: '212 NYC Men Eau de Toilette', name: '212 men' },
  { house: 'Fragrance World', q: 'Harmony Code Absolu', name: 'harmony code absolute' },
  { house: 'Zimaya', q: 'Sharaf Devine', name: 'sharaf divine' },
  { house: 'Maison Crivelli', q: 'Cuir Infrarouge Extrait de Parfum', name: 'cuir infrarouge' },
  { house: 'Chloé', q: 'Chloé Eau de Perfume Naturelle', name: 'chloe eau de parfum naturelle' },
]

function handFound(expectedHouse, query, row) {
  const queryNorm = auditNormalize(query)
  const nameNorm = auditNormalize(row?.name)
  return HAND_FOUND.some(call =>
    auditNormalize(call.q) === queryNorm
    && housesMatch(expectedHouse, call.house)
    && housesMatch(call.house, row?.house)
    && (nameNorm === call.name || containsPhrase(nameNorm, call.name)))
}

function judgeWith(query, expectedHouse, row, matches) {
  if (!row || !row.name) return 'empty'
  if (handFound(expectedHouse, query, row)) return 'found'
  if (!housesMatch(expectedHouse, row.house)) return 'entry_error'
  if (!matches(query, row)) return 'entry_error'
  return 'found'
}

// The order guard keeps a top the shipped matcher already accepted. Judging
// with that same union keeps a held row a success, and still counts a new
// prefix or "for men" match.
function acceptedNameMatch(query, row) {
  const listed = listedQueryGender(query)
  if (listed && !nameCarriesGender(row, listed)) return false
  return strictNameMatch(query, row) || shippedNameMatch(query, row)
}

export function judgeTop(query, expectedHouse, row) {
  return judgeWith(query, expectedHouse, row, acceptedNameMatch)
}

// Same decision the merged FOS-08 search box made, before line-prefix identity.
export function judgeExactTop(query, expectedHouse, row) {
  return judgeWith(query, expectedHouse, row, exactNameMatch)
}

export function isBaselineSuccess(outcome) {
  return outcome === 'found' || outcome === 'easy_add'
}

// Success after a fresh top-1. An empty result stays a success only when the
// baseline itself was an easy-add (the bottle is absent). A new find is a
// success. A wrong bottle is not.
export function isSearchSuccess(baselineOutcome, judgement) {
  if (judgement === 'found') return true
  return judgement === 'empty' && baselineOutcome === 'easy_add'
}

// House phrases the typed text can name. Aliases (ysl, mfk, ch, armani) and
// the catalog's own house names both count. A house that is also an ordinary
// bottle word does not, so "Fresh" and "Couture" do not lock.
const AMBIGUOUS_HOUSE_WORDS = new Set(['fresh', 'couture', 'majesty', 'tabac'])
const HOUSE_LOCK_PHRASES = freezeHouseLockPhrases()

function freezeHouseLockPhrases() {
  const phrases = []
  const add = (phrase, canonical, source) => {
    const text = auditNormalize(phrase)
    const house = auditNormalize(canonical)
    if (!text || !house || text.length < 2) return
    if (source === 'catalog' && AMBIGUOUS_HOUSE_WORDS.has(text)) return
    phrases.push({ phrase: text, canonical: house, source })
  }
  for (const [key, value] of Object.entries(HOUSE_SHORTHAND)) {
    add(key, value, 'alias')
    add(value, value, 'alias')
  }
  for (const [key, value] of Object.entries(HOUSE_LABEL_ALIASES)) {
    add(key, value, 'alias')
    add(value, value, 'alias')
  }
  for (const group of HOUSE_GROUPS) {
    for (const alias of group) add(alias, group[0], 'alias')
  }
  for (const house of CATALOG_HOUSE_NAMES) add(house, house, 'catalog')
  const seen = new Set()
  return phrases.filter(entry => {
    const key = `${entry.phrase}\u0000${entry.canonical}\u0000${entry.source}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

function sameLockedHouse(left, right) {
  return housesMatch(left, right)
}

// The house named by the typed text, if any. Aliases and catalog house names
// both count. An ordinary bottle word that is also a house does not.
// When the query names two houses, an alias wins over a longer catalog name
// ("Dries Van Noten par Frédéric Malle" stays Frédéric Malle). The walk's
// expected house is not an input.
export function queryHouseLock(query, rows = []) {
  void rows
  const q = auditNormalize(query)
  if (!q) return null
  const padded = ` ${q} `
  const hits = []
  for (const entry of HOUSE_LOCK_PHRASES) {
    if (padded.includes(` ${entry.phrase} `)) hits.push(entry)
  }
  if (!hits.length) return null
  const byLength = (a, b) => b.phrase.length - a.phrase.length || a.canonical.localeCompare(b.canonical)
  const aliasHits = hits.filter(hit => hit.source === 'alias').sort(byLength)
  const bestAlias = aliasHits[0]
  let pool = hits
  if (bestAlias) {
    const aliasRival = aliasHits.find(hit => !sameLockedHouse(bestAlias.canonical, hit.canonical) && hit.phrase.length === bestAlias.phrase.length)
    if (aliasRival) return null
    pool = hits.filter(hit => sameLockedHouse(bestAlias.canonical, hit.canonical))
  }
  pool.sort(byLength)
  const best = pool[0]
  const rival = pool.find(hit => !sameLockedHouse(best.canonical, hit.canonical) && hit.phrase.length === best.phrase.length)
  if (rival) return null
  return best.canonical
}

function rowsForHouse(lock, rows) {
  if (!lock) return []
  const kept = []
  const seen = new Set()
  for (const row of rows || []) {
    if (!row || !housesMatch(lock, row.house)) continue
    const id = row.id || `${row.house}\u0000${row.name}\u0000${row.concentration || ''}`
    if (seen.has(id)) continue
    seen.add(id)
    kept.push(row)
  }
  return kept
}

// A shipped top holds when the typed query already identity-matches it.
// shippedNameMatch covers a conflicting gender the newer check rejects, so
// that top stays put instead of being swapped for another house's bottle.
function topAlreadyMatches(query, row) {
  if (!row) return false
  // A listed gender word may not stay on the ungendered bottle, even when
  // the shipped matcher already accepted it ("Code Femme" on Code).
  const listed = listedQueryGender(query)
  if (listed && !nameCarriesGender(row, listed)) return false
  return strictNameMatch(query, row) || shippedNameMatch(query, row)
}

// A long typed title may already be the right row ("I Don't Need a Prince…").
// That same prefix must not pull a longer flanker up from a later page
// ("Hyper Wave", a body spray, a garden edition).
function isPrefixOnlyMatch(query, row) {
  if (!strictNameMatch(query, row)) return false
  const house = auditNormalize(row.house)
  const queryCore = identityParts(query, house).core
  const nameCore = identityParts(row.name, house).core
  if (!queryCore || !isPrefixIdentity(queryCore, nameCore)) return false
  if (queryCore === nameCore || tokenKey(queryCore) === tokenKey(nameCore)) return false
  if (isAffixIdentity(queryCore, nameCore) || oneTokenTypo(queryCore, nameCore)) return false
  return true
}

function replacementMatch(query, row) {
  return strictNameMatch(query, row) && !isPrefixOnlyMatch(query, row)
}

// The wider page is useful only when the shipped top is a real row that is
// not already the typed bottle, and the first page has no such bottle either.
// An empty window stays empty: filling it would turn an absent bottle into
// a different house's same name.
function poolRows(primaryRows, secondRows, recoveryBatches, wideRows) {
  return [
    ...(primaryRows || []),
    ...(secondRows || []),
    ...(recoveryBatches || []).flatMap(batch => batch?.rows || []),
    ...(wideRows?.primary || []),
    ...(wideRows?.second || []),
  ]
}

export function guardNeedsWiderPage(query, primaryRows = [], secondRows = [], recoveryBatches = [], expandedQuery = query) {
  const shipped = selectShipped(query, primaryRows, secondRows, recoveryBatches, expandedQuery)
  const pool = poolRows(primaryRows, secondRows, recoveryBatches)
  const lock = queryHouseLock(query, pool)
  const locked = rowsForHouse(lock, pool)
  // The named house is not on this page. A wider page may have it, and an
  // other-house row must not stay on top once that candidate exists.
  if (lock && shipped[0] && !housesMatch(lock, shipped[0].house) && !locked.length) return true
  if (!shipped[0] || topAlreadyMatches(query, shipped[0])) return false
  return !pool.some(row => replacementMatch(query, row))
}

// Same ordering the search box uses after the RPCs return.
// recoveryBatches are { rpc, rows } in recoveryPlan order. A recovered row
// is used only when it matches, and a v2 batch is ignored when the typed
// query was already rewritten to a real bottle name.
// wideRows is an optional { primary, second } from a larger page. It may
// change the top only when the 77a3d66 top does not already match the typed
// text. The walk's house is not an input.
function applyHouseLock(query, ordered, pool) {
  if (!ordered?.[0]) return ordered
  const lock = queryHouseLock(query, pool)
  if (!lock) return ordered
  const locked = rowsForHouse(lock, [...ordered, ...pool])
  if (!locked.length) return ordered
  if (housesMatch(lock, ordered[0].house)) return ordered.filter(row => housesMatch(lock, row.house))
  const ranked = promoteStrictMatches(query, ruleRank(query, locked, []))
  return ranked.slice(0, ordered.length)
}

export function selectRankedRows(query, primaryRows = [], secondRows = [], recoveryBatches = [], expandedQuery = query, wideRows = null) {
  const shipped = selectShipped(query, primaryRows, secondRows, recoveryBatches, expandedQuery)
  const pool = poolRows(primaryRows, secondRows, recoveryBatches, wideRows)
  const top = shipped[0]
  const lock = queryHouseLock(query, pool)
  const locked = rowsForHouse(lock, pool)
  const foreignTop = lock && locked.length && top && !housesMatch(lock, top.house)
  if (!top || (topAlreadyMatches(query, top) && !foreignTop)) return applyHouseLock(query, shipped, pool)

  const dropPrefixOnly = rows => (rows || []).filter(row => !isPrefixOnlyMatch(query, row))
  const limitToHouse = rows => (lock && locked.length ? rowsForHouse(lock, rows) : rows)
  const poolPrimary = limitToHouse(Array.isArray(wideRows?.primary) && wideRows.primary.length ? wideRows.primary : primaryRows)
  const poolSecond = limitToHouse(Array.isArray(wideRows?.second) ? wideRows.second : secondRows)
  let candidate = promoteStrictMatches(query, ruleRank(query, dropPrefixOnly(poolPrimary), dropPrefixOnly(poolSecond)))
  if (!candidate.some(row => replacementMatch(query, row))) {
    for (const batch of recoveryBatches) {
      if (batch?.rpc === 'v2' && !v2RecoveryAllowed(query, expandedQuery)) continue
      const strictRows = limitToHouse(batch?.rows || []).filter(row => replacementMatch(query, row))
      if (!strictRows.length) continue
      candidate = promoteStrictMatches(query, [...strictRows, ...candidate])
      if (candidate.some(row => replacementMatch(query, row))) break
    }
  }
  let ordered = shipped
  if (candidate[0] && replacementMatch(query, candidate[0])) {
    const lead = candidate[0]
    const rest = (lock && locked.length ? locked : shipped).filter(row => row !== lead && row?.id !== lead?.id)
    // Keep the caller's window. A limit-12 log search must not grow into the
    // 200-row page that was only fetched to recover the top bottle.
    ordered = [lead, ...rest].slice(0, shipped.length)
  }
  return applyHouseLock(query, ordered, pool)
}
